import { useEffect, useState } from "react";
import { Alert, Button, Card, Descriptions, Image, Space, Spin, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getShopOrder } from "@/api/shopOrder";
import type { ShopOrderDetail, ShopOrderRow } from "@/api/shopOrder";
import { formatDateTime } from "@/utils/dateTime";
import { aftersaleStatusLabel, availableOrderOperations, orderOperationLabel } from "./orderOperations";
import type { OrderOperation } from "./orderOperations";
import { OrderRewardsTable } from "./OrderRewardsTable";
import { OrderRemarks } from "./OrderRemarks";
import styles from "../product/ProductDialog.module.css";

const money = (value?: number | null): string => value == null ? "-" : `¥${Number(value).toFixed(2)}`;
const points = (value?: number | null): string => value == null ? "-" : Number(value).toFixed(2);
const statuses = ["待支付", "待发货", "待收货", "已完成", "已取消", "用户申请退款", "退款中", "已退款", "不予退款", "待自提"];
const statusColors = ["gold", "orange", "blue", "green", "default", "magenta", "purple", "cyan", "red", "blue"];
const payTypes: Record<number, string> = { 0: "积分", 1: "佣金金额", 2: "经销商销售收益", 3: "充电收益", 4: "渠道收益" };
const itemColumns: ColumnsType<ShopOrderDetail["orderItems"][number]> = [
  { title: "商品图片", render: (_, item): React.ReactNode => item.goodsImage ? <Image width={64} src={item.goodsImage} /> : "-" },
  { title: "商品名称", dataIndex: "goodsName" },
  { title: "商品规格", dataIndex: "skuName" },
  { title: "售价", dataIndex: "goodsPrice", render: money },
  { title: "购买数量", dataIndex: "goodsNum" },
  { title: "商品小计", dataIndex: "subTotal", render: money },
];
const payColumns: ColumnsType<ShopOrderDetail["payItems"][number]> = [
  { title: "支付方式", render: (_, item): string => payTypes[item.payType] ?? "未知" },
  { title: "支付金额", dataIndex: "payAmount", render: money },
  { title: "手续费", dataIndex: "serviceCharge", render: money },
  { title: "支付状态", render: (_, item): string => ["待支付", "成功", "失败", "已退款"][item.payStatus] ?? "未知" },
];

interface OrderDetailPanelProps {
  row: ShopOrderRow;
  revision?: number;
  onOperate?: (row: ShopOrderRow, action: OrderOperation) => void;
}

export function OrderDetailPanel({ row, revision = 0, onOperate }: OrderDetailPanelProps): React.ReactElement {
  const [detail, setDetail] = useState<ShopOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setLoading(true);
    void getShopOrder(row.id, controller.signal).then((response) => {
      if (controller.signal.aborted) return;
      if (!response.data?.order) {
        void message.error("订单详情加载失败");
        return;
      }
      setDetail(response.data);
    }).catch(() => {
      // 请求层统一提示错误。
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return (): void => controller.abort();
  }, [row.id, revision]);
  const order = detail?.order;
  const aftersale = detail?.aftersale;
  const aftersaleImages = aftersale?.applyImages?.split(",").map((url) => url.trim()).filter(Boolean) ?? [];
  const hasReturnInfo = [aftersale?.returnLogisticsCompany, aftersale?.returnLogisticsCode,
    aftersale?.returnReceiverName, aftersale?.returnReceiverPhone, aftersale?.returnAddress,
    aftersale?.userReturnTime, aftersale?.receiveReturnTime].some((value) => Boolean(value?.trim()));
  const actions = detail ? availableOrderOperations(row.goodsType, detail) : [];
  const handleOperate = (action: OrderOperation): void => {
    if (order) onOperate?.({ ...row, orderStatus: order.orderStatus }, action);
  };
  return <div className={styles.detailLayout}><div className={styles.scrollBody}>
    <Spin spinning={loading}>
      {detail && order && <div className={styles.detailContent}>
        <Card title="订单信息">
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="订单号">{order.orderNo}</Descriptions.Item>
            <Descriptions.Item label="订单状态"><Tag color={statusColors[order.orderStatus] ?? "default"}>{order.orderStatusDesc || statuses[order.orderStatus] || "未知"}</Tag></Descriptions.Item>
            <Descriptions.Item label="下单时间">{formatDateTime(order.createTime || order.payTime)}</Descriptions.Item>
            <Descriptions.Item label="商品类型"><Tag color={row.goodsType === 1 ? "blue" : row.goodsType === 2 ? "purple" : "default"}>{row.goodsType === 1 ? "自营" : row.goodsType === 2 ? "入驻商家" : "-"}</Tag></Descriptions.Item>
          </Descriptions>
        </Card>
        <OrderRemarks orderId={order.id} />
        <Card title="购买商品详情">
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="商家名称">{row.merchantName || "-"}</Descriptions.Item>
          </Descriptions>
          <Table rowKey="id" columns={itemColumns} dataSource={detail.orderItems ?? []} pagination={false} scroll={{ x: "max-content" }} />
        </Card>
        <Card title="下单用户信息">
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="用户ID">{order.appUserId}</Descriptions.Item>
            <Descriptions.Item label="下单用户">{detail.userInfo?.nickName || row.nickName || "-"}</Descriptions.Item>
            <Descriptions.Item label="用户手机号">{detail.userInfo?.mobile || row.mobile || "-"}</Descriptions.Item>
            <Descriptions.Item label="用户备注">{order.userNote || "-"}</Descriptions.Item>
          </Descriptions>
        </Card>
        <Card title="支付信息">
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="商品总金额">{money(order.totalAmount)}</Descriptions.Item>
            <Descriptions.Item label="支付金额">{money(order.payAmount)}</Descriptions.Item>
            {/* <Descriptions.Item label="优惠金额">{money(order.discountAmount)}</Descriptions.Item> */}
            <Descriptions.Item label="邮费">{money(order.postageAmount)}</Descriptions.Item>
          </Descriptions>
          <div style={{marginTop: '20px'}}>
          <Table rowKey="id" columns={payColumns} dataSource={detail.payItems ?? []} pagination={false} scroll={{ x: "max-content" }} />

          </div>
        </Card>
        <Card title={row.goodsType === 1 ? "自营商城分润信息" : "商家分润信息"}>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="是否参与分佣">{order.isCommission === 1 ? "是" : order.isCommission === 0 ? "否" : "-"}</Descriptions.Item>
            <Descriptions.Item label="分佣状态">{order.commissionStatus == null ? "-" : ["未分佣", "待分佣", "已分佣", "分佣失败"][order.commissionStatus] ?? "未知"}</Descriptions.Item>
            {row.goodsType === 2 && <Descriptions.Item label="商家收益">{money(order.merchantIncome)}</Descriptions.Item>}
            <Descriptions.Item label="用户分润（积分）">{points(detail.userRewardAmount)}</Descriptions.Item>
            <Descriptions.Item label="冻结分润（积分）">{points(detail.userRewardFrozenAmount)}</Descriptions.Item>
            <Descriptions.Item label="已撤回分润（积分）">{points(detail.userRewardRefundAmount)}</Descriptions.Item>
            <Descriptions.Item label="平台分润金额">{money(detail.platformCommissionAmount)}</Descriptions.Item>
          </Descriptions>
          <div style={{ color: "#8c8c8c", marginTop: 8 }}>用户分润为本单推荐奖励积分合计，已扣除撤回部分，包含冻结部分。</div>
        </Card>
        {row.goodsType === 2 && <OrderRewardsTable key={order.id} rewards={detail.rewards} />}
        <Card title="收货与物流信息">
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="收货人">{order.receiverName || "-"}</Descriptions.Item>
            <Descriptions.Item label="收货电话">{order.receiverPhone || "-"}</Descriptions.Item>
            <Descriptions.Item label="收货地址" span={2}>{[order.receiverProvince, order.receiverCity, order.receiverDistrict, order.receiverAddress].filter(Boolean).join("") || row.receiveAddress || "-"}</Descriptions.Item>
            <Descriptions.Item label="物流公司">{order.logisticsCompany || "-"}</Descriptions.Item>
            <Descriptions.Item label="物流单号">{order.logisticsCode || "-"}</Descriptions.Item>
            <Descriptions.Item label="发货时间">{formatDateTime(order.deliveryTime)}</Descriptions.Item>
            <Descriptions.Item label="收货时间">{formatDateTime(order.receiveTime)}</Descriptions.Item>
          </Descriptions>
        </Card>
        {aftersale?.id != null && <Card title="售后信息">
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="售后单号">{aftersale.aftersaleNo || "-"}</Descriptions.Item>
            <Descriptions.Item label="售后状态">{aftersaleStatusLabel(detail)}</Descriptions.Item>
            <Descriptions.Item label="售后类型">{aftersale.aftersaleType === 1 ? "仅退款" : aftersale.aftersaleType === 2 ? "退货退款" : "未知"}</Descriptions.Item>
            <Descriptions.Item label="申请退款金额">{money(aftersale.refundAmount)}</Descriptions.Item>
            <Descriptions.Item label="售后商品">{aftersale.goodsName || "-"} {aftersale.skuName}</Descriptions.Item>
            <Descriptions.Item label="申请数量">{aftersale.applyNum ?? "-"}</Descriptions.Item>
            <Descriptions.Item label="用户退款原因" span={2}>{aftersale.applyReason || "-"}</Descriptions.Item>
            <Descriptions.Item label="申请凭证" span={2}><Space wrap>{aftersaleImages.length ? aftersaleImages.map((url) =>
              <Image key={url} width={80} src={url} alt="售后凭证" />) : "-"}</Space></Descriptions.Item>
            <Descriptions.Item label="审核备注" span={2}>{aftersale.auditRemark || "-"}</Descriptions.Item>
            <Descriptions.Item label="用户申请退款时间">{formatDateTime(aftersale.createTime)}</Descriptions.Item>
            <Descriptions.Item label="审核退款时间">{formatDateTime(aftersale.auditTime)}</Descriptions.Item>
            <Descriptions.Item label="退款完成时间">{formatDateTime(aftersale.refundTime)}</Descriptions.Item>
            <Descriptions.Item label="退款流水号" span={2}>{aftersale.refundNo || "-"}</Descriptions.Item>
          </Descriptions>
          {order.orderStatus === 6 && aftersale.aftersaleType === 2 && (aftersale.aftersaleStatus !== 2 ||
            !aftersale.returnLogisticsCompany?.trim() || !aftersale.returnLogisticsCode?.trim()) &&
            <Alert style={{ marginTop: 16 }} type="info" message="等待用户退货并填写完整退货物流，收到退货后可确认收货并退款。" />}
        </Card>}
        {aftersale?.id != null && hasReturnInfo && <Card title="退货信息">
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="退货物流公司" span={2}>{aftersale.returnLogisticsCompany?.trim() || "-"}</Descriptions.Item>
            <Descriptions.Item label="退货物流单号" span={2}>
              <span style={{ overflowWrap: "anywhere" }}>{aftersale.returnLogisticsCode?.trim() || "-"}</span>
            </Descriptions.Item>
            <Descriptions.Item label="退货收货人">{aftersale.returnReceiverName?.trim() || "-"}</Descriptions.Item>
            <Descriptions.Item label="退货联系电话">{aftersale.returnReceiverPhone?.trim() || "-"}</Descriptions.Item>
            <Descriptions.Item label="退货地址" span={2}>{aftersale.returnAddress?.trim() || "-"}</Descriptions.Item>
            <Descriptions.Item label="退货寄出时间">{formatDateTime(aftersale.userReturnTime)}</Descriptions.Item>
            <Descriptions.Item label="退货签收时间">{formatDateTime(aftersale.receiveReturnTime)}</Descriptions.Item>
          </Descriptions>
        </Card>}
      </div>}
    </Spin>
  </div>
    {onOperate && !loading && actions.length > 0 && <div className={styles.actions}>
      {actions.map((action) => <Button key={action} type={action === "deliver" || action === "confirmRefund" || action === "confirmReceive" ? "primary" : "default"}
        danger={action === "rejectRefund" || action === "refuseRefund"} onClick={() => handleOperate(action)}>
        {orderOperationLabel(action, detail)}
      </Button>)}
    </div>}
  </div>;
}
